import axios from 'axios';

const baseUrl = process.env.REACT_APP_BASE_URL;

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && !value.includes('T') && value.includes(',')) {
    return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

const getIndustryNames = (item = {}) => {
  if (Array.isArray(item.industries) && item.industries.length) {
    return item.industries.map((industry) => industry?.name).filter(Boolean);
  }
  if (item.category?.name) return [item.category.name];
  if (item.category) return [String(item.category)];
  return [];
};

const extractData = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === 'object') return payload.data;
  if (Array.isArray(payload)) return payload;
  return payload;
};

export const normalizeBlogPost = (item = {}, index = 0) => {
  const industries = getIndustryNames(item);
  const title = item.title || 'Untitled post';
  const slug = item.slug || `post-${item.id || index}`;

  const rawContent = item.content ?? item.body ?? item.html ?? item.content_html ?? '';
  const contentHtml = typeof rawContent === 'string' ? rawContent : '';
  const bodyBlocks = Array.isArray(rawContent)
    ? rawContent
    : Array.isArray(item.blocks)
      ? item.blocks
      : null;

  return {
    id: item.id || slug || `api-${index}`,
    slug,
    title,
    date: formatDate(item.created_at || item.updated_at || item.date),
    createdAt: item.created_at || item.updated_at || null,
    category: industries[0] || 'Insights',
    industries,
    summary: item.excerpt || item.summary || item.description || '',
    author: item.author_name || item.author?.name || 'Sonaroid Author',
    image: item.cover_image_url || item.cover_image || item.image || '',
    featured: Boolean(item.is_featured),
    status: item.status,
    isActive: item.is_active !== false,
    contentHtml,
    bodyBlocks,
    raw: item,
  };
};

export const fetchBlogList = async () => {
  const response = await axios.get(`${baseUrl}blog`);
  const list = extractData(response.data);
  const items = Array.isArray(list) ? list : [];

  return items
    .map((item, index) => normalizeBlogPost(item, index))
    .filter((post) => post.isActive && (!post.status || post.status === 'published'))
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
};

export const fetchBlogBySlug = async (slug) => {
  const response = await axios.get(`${baseUrl}blog/${slug}`);
  const data = extractData(response.data);
  if (!data || Array.isArray(data)) {
    throw new Error('Blog post not found');
  }
  return normalizeBlogPost(data);
};

export const fetchRelatedPosts = async (slug, limit = 3) => {
  const response = await axios.get(`${baseUrl}blog/${slug}/related`, { params: { limit } });
  const list = extractData(response.data);
  const items = Array.isArray(list) ? list : [];
  return items.map((item, index) => normalizeBlogPost(item, index)).slice(0, limit);
};

/** Featured = latest 3 posts; Latest = full list for category filtering */
export const splitBlogLists = (posts = []) => {
  const sorted = [...posts].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  return {
    featuredPosts: sorted.slice(0, 3),
    latestPosts: sorted,
  };
};

export const buildCategories = (posts = []) => {
  const industryNames = posts.flatMap((post) =>
    post.industries?.length ? post.industries : [post.category]
  );

  return ['All Categories', ...Array.from(new Set(industryNames.filter(Boolean)))];
};
